import React from "react";
import {
  ArrowUpRight,
  CheckCheck,
  Image as ImageIcon,
  MessageCircleMore,
  Paperclip,
} from "lucide-react";
import AvatarImage from "../../ui/Avatar/AvatarImage";

interface ChatMessageToastProps {
  authorId: string;
  authorName: string;
  message: string;
  attachmentKind?: "image" | "file";
  onMarkAsRead: () => void;
  onReply: () => void;
}

const stopAndRun = (
  event: React.MouseEvent<HTMLButtonElement>,
  action: () => void,
) => {
  event.preventDefault();
  event.stopPropagation();
  action();
};

const ChatMessageToastStyles = () => (
  <style>{`
    /*
     * The application still loads Bootstrap, whose .bg-white utility is
     * !important. These component-scoped rules deliberately own the toast's
     * dark-mode surfaces so notification actions cannot flash white.
     */
    .dark .chat-message-toast {
      color: #F4F4F5;
    }

    .dark .chat-message-toast-badge {
      background: rgba(126, 87, 194, 0.17) !important;
      color: #D8C8F5 !important;
      border: 1px solid rgba(196, 167, 243, 0.10);
    }

    .dark .chat-message-toast-preview {
      background: #1D202B !important;
      border-color: rgba(255, 255, 255, 0.065) !important;
      color: #D4D4D8 !important;
    }

    .dark .chat-message-toast-secondary {
      background: #1D202B !important;
      border-color: rgba(255, 255, 255, 0.075) !important;
      color: #D4D4D8 !important;
      box-shadow: none !important;
    }

    .dark .chat-message-toast-secondary:hover {
      background: #282C39 !important;
      border-color: rgba(167, 139, 250, 0.24) !important;
      color: #D8C8F5 !important;
    }

    .dark .chat-message-toast-primary {
      background: #7E57C2 !important;
      color: #FFFFFF !important;
      box-shadow: none !important;
    }

    .dark .chat-message-toast-primary:hover {
      background: #6D4DB3 !important;
    }

    .dark .chat-message-toast-icon {
      background: rgba(126, 87, 194, 0.15) !important;
      border-color: rgba(196, 167, 243, 0.14) !important;
      color: #C4A7F3 !important;
    }

    /*
     * Toastify's wrapper is outside this component, but modern browsers let
     * us safely style only Toastify toasts that contain this component.
     */
    .dark .Toastify__toast:has(.chat-message-toast) {
      background: #171A24 !important;
      border: 1px solid rgba(255, 255, 255, 0.07) !important;
      border-radius: 16px !important;
      color: #F4F4F5 !important;
      box-shadow: 0 16px 36px rgba(0, 0, 0, 0.38) !important;
    }

    .dark .Toastify__toast:has(.chat-message-toast) .Toastify__toast-body {
      color: inherit !important;
    }

    .dark .Toastify__toast:has(.chat-message-toast) .Toastify__close-button {
      color: #A1A1AA !important;
      opacity: .8;
    }

    .dark .Toastify__toast:has(.chat-message-toast) .Toastify__close-button:hover {
      color: #F4F4F5 !important;
      opacity: 1;
    }
  `}</style>
);

export const ChatMessageToast: React.FC<ChatMessageToastProps> = ({
  authorId,
  authorName,
  message,
  attachmentKind,
  onMarkAsRead,
  onReply,
}) => {
  const handleCardClick = (event?: React.MouseEvent<HTMLDivElement>) => {
    if (
      event?.target instanceof HTMLElement &&
      event.target.closest("button, a, [data-toast-interactive='true']")
    ) {
      return;
    }

    onReply();
  };

  return (
    <>
      <ChatMessageToastStyles />
      <div
      className="chat-message-toast relative min-w-0 flex-1 cursor-pointer overflow-hidden px-0.5 py-0.5"
      onClick={handleCardClick}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onReply();
        }
      }}
      role="button"
      tabIndex={0}
    >
      <span
        className="absolute -left-3 top-0 h-full w-[3px] rounded-r-full bg-[#7E57C2]"
        aria-hidden="true"
      />

      <div className="flex min-w-0 items-start gap-3">
        <AvatarImage
          userId={authorId}
          displayName={authorName}
          size="xs"
          alternate
          className="shrink-0"
        />

        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-2">
            <p className="m-0 truncate text-[13px] font-semibold leading-tight text-gray-900 dark:text-zinc-100">
              {authorName}
            </p>
            <span className="chat-message-toast-badge shrink-0 rounded-md bg-purple-50 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.08em] text-[#6D4DB3]">
              New message
            </span>
          </div>

          <div className="chat-message-toast-preview mt-1.5 flex min-w-0 items-start gap-2 rounded-xl border border-gray-100 bg-gray-50/80 px-2.5 py-2">
            {attachmentKind === "image" ? (
              <ImageIcon
                size={14}
                className="mt-0.5 shrink-0 text-[#7E57C2]"
                aria-hidden="true"
              />
            ) : attachmentKind === "file" ? (
              <Paperclip
                size={14}
                className="mt-0.5 shrink-0 text-[#7E57C2]"
                aria-hidden="true"
              />
            ) : null}
            <p className="m-0 line-clamp-2 break-words text-[12px] font-normal leading-relaxed text-gray-600 dark:text-zinc-300">
              {message}
            </p>
          </div>
        </div>
      </div>

      <div className="mt-2.5 flex items-center justify-end gap-2">
        <button
          type="button"
          className="chat-message-toast-secondary inline-flex h-8 items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 text-[11px] font-semibold text-gray-600 transition-colors hover:border-purple-200 hover:bg-purple-50 hover:text-[#6D4DB3]"
          data-toast-interactive="true"
          onClick={(event) => stopAndRun(event, onMarkAsRead)}
        >
          <CheckCheck size={13} aria-hidden="true" />
          Mark read
        </button>
        <button
          type="button"
          className="chat-message-toast-primary inline-flex h-8 items-center gap-1.5 rounded-lg bg-[#7E57C2] px-3 text-[11px] font-semibold text-white shadow-sm transition-colors hover:bg-[#6D4DB3]"
          data-toast-interactive="true"
          onClick={(event) => stopAndRun(event, onReply)}
        >
          Reply
          <ArrowUpRight size={13} aria-hidden="true" />
        </button>
      </div>
    </div>
    </>
  );
};

interface UnreadMessagesToastProps {
  count: number;
  onViewMessages: () => void;
}

export const UnreadMessagesToast: React.FC<UnreadMessagesToastProps> = ({
  count,
  onViewMessages,
}) => (
  <>
    <ChatMessageToastStyles />
    <div className="chat-message-toast chat-message-toast-unread relative min-w-0 flex-1 overflow-hidden px-0.5 py-0.5">
    <span
      className="absolute -left-3 top-0 h-full w-[3px] rounded-r-full bg-[#7E57C2]"
      aria-hidden="true"
    />

    <div className="flex items-start gap-3">
      <div className="chat-message-toast-icon flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-purple-100 bg-purple-50 text-[#6D4DB3]">
        <MessageCircleMore size={19} strokeWidth={2} aria-hidden="true" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="m-0 text-[13px] font-semibold leading-snug text-gray-900 dark:text-zinc-100">
          Messages waiting
        </p>
        <p className="mb-0 mt-1 text-[12px] font-normal leading-relaxed text-gray-600 dark:text-zinc-300">
          You have {count} unread conversation{count === 1 ? "" : "s"}.
        </p>
      </div>
    </div>

    <div className="mt-2.5 flex justify-end">
      <button
        type="button"
        className="chat-message-toast-primary inline-flex h-8 items-center gap-1.5 rounded-lg bg-[#7E57C2] px-3 text-[11px] font-semibold text-white shadow-sm transition-colors hover:bg-[#6D4DB3]"
        onClick={(event) => stopAndRun(event, onViewMessages)}
      >
        View messages
        <ArrowUpRight size={13} aria-hidden="true" />
      </button>
    </div>
  </div>
  </>
);